/*
 *
 * Pure functions of their arguments so the rules can be tested without rendering a grid, in the
 * shape of sparePartSearch.js and workOrderSearch.js. The matching rule itself is shared with
 * every other CMMS search box; see features/cmms/cmmsSearch.js for why each word is matched
 * separately rather than the whole query as one substring.
 *
 * Client-side, and here it could not be otherwise: the page holds the whole tree already — the
 * table renders the same query the tree does — and there is no endpoint that searches it.
 */
import { getAssetNodeTypeLabel } from "./cmmsMessages";
import { filterBySearchText } from "./cmmsSearch";

/*
 * The row as it reads on screen, plus its ancestry, lower-cased.
 *
 * The ancestry is the field that makes this worth having. A maintenance object is called "Press 3"
 * and lives under "Montering" under "Site Alfa", and the thing someone types is a word from the
 * object and a word from where they think it is. Folding every ancestor name in — not just the
 * parent — lets "alfa press" land on it as readily as "montering press", which is the same
 * combining the register's box allows across its own fields.
 *
 * `parentName` is listed as well even though it is the last of `ancestorNames`: the two come from
 * the same flatten and duplicating one word costs nothing, while relying on that identity would
 * make this quietly wrong the first time either is computed differently.
 *
 * nodeType is a number on the row, so it is resolved the way the grid's own column resolves it —
 * otherwise "avdelning" would find nothing while the table plainly shows the word. Guarded rather
 * than passed straight through: getAssetNodeTypeLabel runs its argument through Number(), and a
 * node arriving without a type would otherwise contribute the literal text "NaN".
 *
 * The nameplate's text is in: a serial number off the machine's plate is exactly the kind of
 * thing someone types to find it. The model year is not. Every word is matched as a substring, and
 * every year since 2000 contains "20", so "press 20" would find every press built this century.
 *
 * The node id is deliberately absent, for the reason workOrderSearch.js gives: the column is
 * hidden by default, and a bare number makes "12" match every id that merely contains it.
 */
export const getAssetNodeSearchText = (node, context) => {
  const { intl } = context ?? {};

  return [
    node?.name,
    node?.description,
    node?.parentName,
    node?.linkedMachineName,
    node?.manufacturer,
    node?.model,
    node?.serialNumber,
    node?.nodeType ? getAssetNodeTypeLabel(node.nodeType, intl) : null,
    ...(node?.ancestorNames ?? []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/*
 * The nodes matching every word of the query, in any field and any order.
 *
 * `context` carries the intl instance the type label needs. Passed per call rather than closed
 * over, as in workOrderSearch.js: the language can change under a mounted page.
 */
export const filterAssetNodes = (nodes, query, context) =>
  filterBySearchText(nodes, query, (row) => getAssetNodeSearchText(row, context));

/*
 * The ids the tree keeps on screen for a query: each match plus the path down to it, since a match
 * the tree cannot reach is not really shown. Built on filterAssetNodes, so the tree and the table
 * agree on what matches.
 *
 * `null` for a blank query rather than every id. The caller has to tell "no filter" from "a filter
 * that happens to keep everything": the first leaves the user's own expansion alone, the second
 * opens the tree down to the matches.
 */
export const getTreeSearchVisibleIds = (nodes, query, context) => {
  if (!query?.trim()) return null;

  return new Set(
    filterAssetNodes(nodes, query, context).flatMap((node) => [...(node.ancestorIds ?? []), node.id]),
  );
};
